<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { formatDate } from '@/utils/dateFormatter'
import { IExtendedDegree, IExtendedDegreePackage } from '@shared/types/extensions'
import type { ISepResult } from '@shared/types/package'
import { degreeService } from '@/services/degreeService'
import { downloadService } from '@/services/downloadService'
import { ref as dbRef, get, query, orderByChild, equalTo } from 'firebase/database'
import { database } from '@/firebase/config'
import { getAuth } from 'firebase/auth'
import { useStore } from '@/store'
import SendToSEPModal from '@/components/SendToSEPModal.vue'
import RequestSignDegreeModal from '@/components/titulos/RequestSignDegreeModal.vue'
import PackageDetailsModal from '@/components/PackageDetailsModal.vue'
import BaseModal from '@/components/BaseModal.vue'
import { Assets } from '@/types/assets'
const auth = getAuth()
const db = database
const store = useStore()
const assets = Assets.getInstance()
const showErrorModal = ref(false)
const errorMessage = ref<string>('')
const showSuccessModal = ref(false)
const successMessage = ref<string>('')
const props = defineProps<{
	packages: IExtendedDegreePackage[]
	institutionId: string
	testMode: boolean
}>()

const emit = defineEmits<(e: 'package-removed' | 'refresh-packages') => void>()

// Estado local reactivo para los paquetes
const localPackages = ref<IExtendedDegreePackage[]>([])
// Sincronizar props con estado local
watch(
	() => props.packages,
	newPackages => {
		localPackages.value = [...newPackages]
	},
	{ immediate: true, deep: true }
)

// Watcher adicional para detectar cambios específicos en los paquetes
watch(
	() => props.packages,
	(newPackages, oldPackages) => {
		if (oldPackages && newPackages) {
			// Verificar si algún paquete cambió su estado de firma
			const hasSignatureChange = newPackages.some((newPkg, index) => {
				const oldPkg = oldPackages[index]
				return oldPkg && newPkg.id === oldPkg.id && newPkg.signed !== oldPkg.signed
			})

			if (hasSignatureChange) {
				localPackages.value = [...newPackages]
			}
		}
	},
	{ deep: true }
)

const columns = [
	{ key: 'id', label: 'ID' },
	{ key: 'label', label: 'Nombre' },
	{ key: 'fecha', label: 'Fecha' },
	{ key: 'sign', label: 'Estado' },
	{ key: 'actions', label: 'Acciones' }
]

// Send to SEP
const showSendToSEPModal = ref(false)
const selectedPackage = ref<IExtendedDegreePackage | null>(null)
const selectedPackageId = ref<number>(0)
const loading = ref(false)
const selectedPackageIndex = ref<number | null>(null)
const selectedPackageCancel = ref<IExtendedDegreePackage>({} as IExtendedDegreePackage)
const showPackageDetailsModal = ref(false)
const showSignModal = ref(false)
const showConfirmModal = ref(false)
const showConfirmCancelPackage = ref(false)
const cadenasAFirmar = ref<string[]>([])
const degreesToSign = ref<IExtendedDegree[]>([])
const deviceId = ref<string>('')
const showErrorSignModal = ref(false)
const errorSignMessage = ref('')
const responsablesDegree = ref<string[]>([])
// Obtener el CURP del usuario autenticado
const userCurp = computed(() => store.state.global.user.me.curp || '')

const openSendToSEPModal = (packageData: IExtendedDegreePackage) => {
	selectedPackage.value = packageData
	showSendToSEPModal.value = true
}

// Firebase paths
const getInstitutionPath = (path: string) => `institutions/${props.institutionId}${props.testMode ? '/testMode' : ''}${path}`

// Remove package
const removePackage = async (index: number) => {
	const package_ = localPackages.value[index]
	if (!package_) return

	if (!confirm('¿Está seguro de eliminar este paquete? Los títulos volverán a estar disponibles para crear un nuevo paquete.')) {
		return
	}
	try {
		loading.value = true
		await degreeService.deletePackage(package_.id, props.institutionId, props.testMode)
		emit('package-removed')
		localPackages.value.splice(index, 1)
	} catch (error) {
		console.error('Error al eliminar el paquete:', error)
		let errorMessageValue = 'Ocurrió un error al eliminar el paquete, intentelo nuevamente. Si el problema persiste, solicite ayuda en ' + assets.vendorEmail
		if (error instanceof Error) {
			errorMessageValue = error.message
		}
		showErrorModal.value = true
		errorMessage.value = errorMessageValue
	} finally {
		loading.value = false
	}
}

// Download package
const downloadPackage = async (index: number) => {
	const package_ = localPackages.value[index]
	if (!package_) return

	try {
		loading.value = true
		await downloadService.downloadPackage({
			packageId: package_.id,
			packageLabel: package_.label,
			institutionId: props.institutionId,
			testMode: props.testMode
		})
	} catch (error) {
		console.error('Error downloading package:', error)
		if (error instanceof Error) {
			showErrorModal.value = true
			errorMessage.value = error.message
		} else {
			showErrorModal.value = true
			errorMessage.value = 'No fue posible descargar el paquete'
		}
	} finally {
		loading.value = false
	}
}

// View package details
const viewPackageDetails = async (index: number) => {
	const package_ = localPackages.value[index]
	if (!package_) return

	try {
		loading.value = true
		selectedPackageIndex.value = index
		showPackageDetailsModal.value = true
	} catch (error) {
		console.error('Error viewing package details:', error)
		showErrorModal.value = true
		errorMessage.value = 'No fue posible ver los detalles del paquete'
	} finally {
		loading.value = false
	}
}

// Abrir modal de firma
const paqueteFirmado = ref(false)
const openSignModal = async (index: number) => {
	selectedPackageIndex.value = index
	const packageToSign = localPackages.value[index]

	try {
		loading.value = true
		paqueteFirmado.value = packageToSign.signed || false

		// Si el paquete ya está firmado, no necesitamos cargar las cadenas
		if (paqueteFirmado.value) {
			showSignModal.value = true
			return
		}

		// Verificar que el usuario tenga CURP configurado
		if (!userCurp.value) {
			throw new Error('No tienes CURP configurado. Contacta a soporte para configurar tu CURP.')
		}

		// Obtener las cadenas originales del paquete
		const degreesRef = dbRef(db, getInstitutionPath('/degrees/queue'))
		const packageQuery = query(degreesRef, orderByChild('package'), equalTo(packageToSign.id))
		const snapshot = await get(packageQuery)

		if (!snapshot.exists()) {
			errorSignMessage.value = 'No se encontraron títulos en el paquete'
			showErrorSignModal.value = true
			return
		}

		const documents = snapshot.val()
		cadenasAFirmar.value = []
		degreesToSign.value = []

		// Recolectar solo las cadenas originales que correspondan al CURP del usuario
		for (const [, degreeData] of Object.entries(documents)) {
			const degree = degreeData as IExtendedDegree & {
				cadenaOriginal: string | Record<string, string>
			}
			Object.keys(degree.data?.FirmaResponsables || {}).forEach(curp => {
				if (!responsablesDegree.value.includes(curp)) {
					responsablesDegree.value.push(curp)
				}
			})
			// Verificar que el usuario esté asignado como responsable en este título
			if (!degree.data?.FirmaResponsables?.[userCurp.value]) {
				errorSignMessage.value = `El usuario con CURP ${userCurp.value} no está asignado como responsable en el título ${degree.folioControl}`
				showErrorSignModal.value = true
				continue
			}

			degreesToSign.value.push(degree)

			if (typeof degree.cadenaOriginal === 'object') {
				// Si cadenaOriginal es un objeto, buscar la cadena específica del usuario
				const cadenaUsuario = degree.cadenaOriginal[userCurp.value]
				if (cadenaUsuario) {
					cadenasAFirmar.value.push(cadenaUsuario)
				} else {
					errorSignMessage.value = `No se encontró cadena original para el CURP ${userCurp.value} en el título ${degree.folioControl}`
					showErrorSignModal.value = true
				}
			} else if (degree.cadenaOriginal) {
				// Si cadenaOriginal es un string, agregarlo directamente
				cadenasAFirmar.value.push(degree.cadenaOriginal)
			}
		}

		if (cadenasAFirmar.value.length === 0) {
			errorSignMessage.value = `No se encontraron cadenas originales para firmar con tu CURP (${userCurp.value}).`
			showErrorSignModal.value = true
			return
		}

		// Obtener el deviceId usando el ID del usuario conectado
		if (auth.currentUser?.uid) {
			deviceId.value = `WEB.${auth.currentUser.uid}`
		} else {
			throw new Error('Usuario no autenticado')
		}

		showSignModal.value = true
	} catch (error: any) {
		console.error('Error al preparar el paquete para firma:', error)
		showErrorModal.value = true
		errorMessage.value = `Error: ${error.message}`
	} finally {
		loading.value = false
	}
}

const handlePackageUpdate = (updatedPackage: IExtendedDegreePackage) => {
	selectedPackage.value = updatedPackage

	// Actualizar el paquete en el estado local
	const index = localPackages.value.findIndex(p => p.id === updatedPackage.id)
	if (index !== -1) {
		localPackages.value[index] = { ...updatedPackage }
	}
}

const refreshPackages = () => {
	emit('refresh-packages')
}

const cancelPackage = async (packageId: number) => {
	try {
		// Confirmar la acción antes de proceder
		// const confirmMessage = `¿Estás seguro de que deseas cancelar el paquete ${packageId}? Esta acción no se puede deshacer.`
		// if (!confirm(confirmMessage)) {
		// 	return
		// }

		// Mostrar indicador de carga
		showConfirmCancelPackage.value = false
		loading.value = true

		const response = await degreeService.cancelPackageDegrees(props.institutionId, packageId, props.testMode)

		// Mostrar mensaje apropiado según el código de respuesta
		if (response && response.message) {
			if (response.code === 2) {
				// Títulos ya cancelados previamente
				showSuccessModal.value = true
				successMessage.value = `Información: ${response.message}`
			} else if (response.code === 1) {
				// Éxito en la cancelación
				showSuccessModal.value = true
				successMessage.value = `Éxito: ${response.message}`
			} else {
				// Otros casos
				showSuccessModal.value = true
				successMessage.value = `Resultado: ${response.message}`
			}
		} else {
			showSuccessModal.value = true
			successMessage.value = 'Paquete procesado exitosamente'
		}

		// Refrescar la lista de paquetes
		emit('refresh-packages')
	} catch (error: any) {
		console.error('Error al cancelar el paquete:', error)

		// Determinar el mensaje de error específico
		let errorMessageValue = 'Error al cancelar el paquete'

		if (error.message) {
			// Si el error ya viene formateado del servicio, usarlo directamente
			if (error.message.includes('Error al cancelar el paquete')) {
				errorMessageValue = error.message
			} else {
				// Formatear el mensaje de error para el usuario
				errorMessageValue = `Error al cancelar el paquete ${packageId}: ${error.message}`
			}
		}

		showErrorModal.value = true
		errorMessage.value = errorMessageValue
	} finally {
		// Ocultar indicador de carga
		loading.value = false
	}
}
const cancelarTitulosConError = async (packageData: IExtendedDegreePackage) => {
	showConfirmModal.value = false

	const respuestas: ISepResult[] = Array.isArray(packageData.sepResult) ? packageData.sepResult : Object.values(packageData.sepResult ?? {})
	const titulosConError = respuestas.filter(respuesta => respuesta.ESTATUS == 2 && String(respuesta['FOLIO CONTROL'] || '').trim())

	if (titulosConError.length === 0) {
		showErrorModal.value = true
		errorMessage.value = 'No hay títulos con respuesta de la SEP con error para cancelar'
		return
	}

	loading.value = true
	try {
		const resultados = await Promise.allSettled(titulosConError.map(respuesta => degreeService.cancelDegree(props.institutionId, String(respuesta['FOLIO CONTROL']).trim(), props.testMode)))
		const fallos = resultados.filter(resultado => resultado.status === 'rejected')

		if (fallos.length > 0) {
			const mensajes = [...new Set(fallos.map(resultado => (resultado.reason as Error)?.message || 'No es posible cancelar este título'))]
			showErrorModal.value = true
			errorMessage.value = `No se cancelaron ${fallos.length} de ${titulosConError.length} título(s). ${mensajes.join(' ')}`
		} else {
			showSuccessModal.value = true
			successMessage.value = `Se cancelaron ${titulosConError.length} título(s) con error`
		}

		emit('refresh-packages')
	} catch (error: any) {
		console.error('Error al cancelar títulos con error:', error)
		showErrorModal.value = true
		errorMessage.value = error?.message || 'Error al cancelar los títulos con error'
	} finally {
		loading.value = false
	}
}
const closeErrorSignModal = () => {
	showErrorSignModal.value = false
	responsablesDegree.value = []
	errorSignMessage.value = ''
}
const showCancelModal = (packageData: IExtendedDegreePackage) => {
	showConfirmModal.value = true
	selectedPackageCancel.value = packageData
}
const confirmCancelPackage = (packageId: number) => {
	showConfirmCancelPackage.value = true
	selectedPackageId.value = packageId
}
</script>
<template>
	<div class="package-view-container">
		<VaDataTable :items="localPackages" :columns="columns" sticky-header class="package-table">
			<template #cell(id)="{ rowIndex }">
				<div class="package-id-cell">
					<VaIcon name="inventory_2" color="primary" class="package-icon" />
					<span class="package-id-text">{{ localPackages[rowIndex].id }}</span>
				</div>
			</template>

			<template #cell(label)="{ rowIndex }">
				<div class="package-label-cell">
					<VaIcon name="description" color="primary" class="label-icon" />
					<span class="package-label-text">{{ localPackages[rowIndex].label }}</span>
				</div>
			</template>

			<template #cell(fecha)="{ rowIndex }">
				<div class="package-date-cell">
					<VaIcon name="event" color="primary" class="date-icon" />
					<span class="package-date-text">{{ formatDate(localPackages[rowIndex].id) }}</span>
				</div>
			</template>

			<template #cell(sign)="{ rowIndex }">
				<div class="package-status-cell">
					<VaBadge v-if="localPackages[rowIndex].sign" color="transparent" text="Firmado" class="status-badge status-signed">
						<template #prepend>
							<VaIcon name="check_circle" class="status-icon" />
						</template>
					</VaBadge>
					<VaBadge v-else color="transparent" text="Pendiente" class="status-badge status-pending">
						<template #prepend>
							<VaIcon name="pending" class="status-icon" />
						</template>
					</VaBadge>
				</div>
			</template>

			<template #cell(actions)="{ rowIndex }">
				<div class="package-actions-cell">
					<div class="actions-container">
						<!-- Ver detalles -->
						<VaPopover message="Ver detalles del paquete" class="action-popover">
							<VaButton preset="plain" icon="visibility" class="action-button" @click="viewPackageDetails(rowIndex)" />
						</VaPopover>

						<!-- Solicitar firma -->
						<VaPopover v-if="!localPackages[rowIndex].sign" message="Solicitar firma del paquete" class="action-popover">
							<VaButton preset="plain" icon="edit_square" class="action-button btn-primary" @click="openSignModal(rowIndex)" />
						</VaPopover>

						<!-- Enviar a la SEP -->
						<VaPopover v-if="localPackages[rowIndex].sign" message="Enviar a la SEP" class="action-popover">
							<VaButton preset="plain" icon="send" class="action-button btn-primary" @click="openSendToSEPModal(localPackages[rowIndex])" />
						</VaPopover>

						<!-- Descargar títulos -->
						<VaPopover v-if="localPackages[rowIndex].sign" message="Descargar títulos" class="action-popover">
							<VaButton preset="plain" icon="download" class="action-button" @click="downloadPackage(rowIndex)" />
						</VaPopover>

						<!-- Eliminar paquete -->
						<VaPopover v-if="!localPackages[rowIndex].sign" message="Eliminar paquete" class="action-popover">
							<VaButton preset="plain" icon="delete" color="danger" class="action-button" @click="removePackage(rowIndex)" :disabled="localPackages[rowIndex].sign" />
						</VaPopover>

						<!-- Cancelar paquete -->
						<VaPopover v-if="localPackages[rowIndex].sepResult && localPackages[rowIndex].sepResult.length > 0" message="Cancelar paquete previamente firmado" class="action-popover">
							<VaButton preset="plain" icon="cancel" color="danger" class="action-button" @click="confirmCancelPackage(localPackages[rowIndex].id)" :disabled="!localPackages[rowIndex].sign" />
						</VaPopover>
						<VaPopover v-if="localPackages[rowIndex].sepResult && localPackages[rowIndex].sepResult.length > 0" message="Cancelar títulos con error" class="action-popover">
							<VaButton
								preset="plain"
								icon="cancel_schedule_send"
								color="danger"
								class="action-button"
								@click="showCancelModal(localPackages[rowIndex])"
								:disabled="!localPackages[rowIndex].sepResult.some(respuesta => respuesta.ESTATUS === 2)" />
						</VaPopover>
					</div>
				</div>
			</template>
		</VaDataTable>
	</div>

	<!-- Modales -->
	<SendToSEPModal
		v-if="selectedPackage && showSendToSEPModal"
		v-model="showSendToSEPModal"
		:package="selectedPackage"
		:institution="institutionId"
		:test-mode="testMode"
		@update:package="handlePackageUpdate" />

	<PackageDetailsModal
		v-if="showPackageDetailsModal && selectedPackageIndex !== null"
		v-model="showPackageDetailsModal"
		:package-data="localPackages[selectedPackageIndex]"
		:institution-id="institutionId"
		:test-mode="testMode"
		@update:modelValue="
			val => {
				showPackageDetailsModal = val
			}
		" />

	<RequestSignDegreeModal
		v-model="showSignModal"
		:package="localPackages[selectedPackageIndex || 0]"
		:cadenas-a-firmar="cadenasAFirmar"
		:test-mode="testMode"
		:institution-id="institutionId"
		:device-id="deviceId"
		@refresh-packages="refreshPackages"
		@update:modelValue="val => (showSignModal = val)" />
	<!-- Modal de Firma -->
	<BaseModal v-model="showErrorSignModal" title="Advertencia" :test-mode="testMode" :message="errorSignMessage" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Advertencia en la Firma</h3>
				</div>

				<div class="error-content">
					<p class="error-description">{{ errorSignMessage }}</p>

					<div class="responsables-info" v-if="responsablesDegree.length > 0">
						<div class="responsables-header">
							<i class="fas fa-users"></i>
							<h4>Responsables asignados en los títulos:</h4>
						</div>
						<div class="responsables-list">
							<div v-for="degree in responsablesDegree" :key="degree" class="responsable-item">
								<i class="fas fa-user-graduate"></i>
								<span>{{ degree }}</span>
								<VaBadge color="transparent" text="Pendiente de firma" class="status-badge status-pending"></VaBadge>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="closeErrorSignModal">Entendido</button>
		</template>
	</BaseModal>
	<BaseModal v-model="showSuccessModal" title="Éxito" :test-mode="testMode" :message="successMessage" size="medium">
		<template #body>
			<div class="success-message">
				<div class="success-header">
					<div class="success-icon">
						<i class="fas fa-check-circle"></i>
					</div>
					<h3 class="success-title">Éxito</h3>
				</div>
				<p class="success-description">{{ successMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showSuccessModal = false">Entendido</button>
		</template>
	</BaseModal>
	<BaseModal v-model="showErrorModal" title="Error" :test-mode="testMode" :message="errorMessage" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errorMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
	<BaseModal v-model="showConfirmModal" title="Confirmación" message="¿Estás seguro de querer cancelar los títulos con error?" :test-mode="false" size="medium">
		<template #body>
			<div class="confirm-message">
				<p class="confirm-message-text" style="text-align: center">
					<b>¿Estás seguro de querer cancelar los títulos con error del paquete {{ selectedPackageCancel.id }}?</b>
				</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showConfirmModal = false">Cancelar</button>
			<button class="btn btn-primary" @click="cancelarTitulosConError(selectedPackageCancel || {})">Eliminar</button>
		</template>
	</BaseModal>
	<BaseModal v-model="showConfirmCancelPackage" title="Confirmación" message="¿Estás seguro de querer cancelar el paquete?" :test-mode="false" size="medium">
		<template #body>
			<div class="confirm-message">
				<p class="confirm-message-text" style="text-align: center">
					<b>¡Atención! Al confirmar esta acción, se cancelarán todos los títulos electrónicos asociados a este paquete.</b>
				</p>
				<br />
				<p class="confirm-message-text" style="text-align: center">
					<span class="text-danger"><b>Esta acción es definitiva y no se puede deshacer.</b></span>
				</p>
				<br />
				<p class="confirm-message-text" style="text-align: center">
					<b>¿Desea continuar con la cancelación del paquete {{ selectedPackageId }}?</b>
				</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showConfirmCancelPackage = false">Cancelar</button>
			<button class="btn btn-primary" @click="cancelPackage(selectedPackageId)">Eliminar</button>
		</template>
	</BaseModal>
</template>

<style scoped>
.success-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #28a745;
}
.success-message {
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border: 2px solid #28a745;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInSuccess 0.4s ease-out;
}

.success-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #ffffff;
}

.success-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.success-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.success-title {
	margin: 0;
	color: #ffffff;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
/* Contenedor Principal - sin height: 100% para que crezca con la tabla y el scroll de la página muestre todo */
.package-view-container {
	overflow: visible;
	padding: 1rem;
}

/* Tabla Mejorada - altura según contenido para no recortar filas al final */
.package-table {
	height: auto;
	border-radius: 12px;
	overflow: visible;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	animation: fadeIn 0.5s ease-in-out;
}

/* Estilos de la Tabla VaDataTable */
.package-table :deep(.va-data-table) {
	height: auto;
	border-radius: 12px;
	overflow: hidden;
}

.package-table :deep(.va-data-table__table-wrapper) {
	height: auto;
	overflow: visible;
	border-radius: 12px;
}

.package-table :deep(.va-data-table__table) {
	width: 100%;
	border-collapse: collapse;
	font-family: Arial, sans-serif;
	background-color: #f9f9f9;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	overflow: hidden;
	border: 2px solid #1a4d42;
	border-radius: 8px;
}

.package-table :deep(.va-data-table__table-container) {
	height: auto;
	overflow: visible;
	border-radius: 12px;
}

/* Header de la Tabla */
.package-table :deep(.va-data-table__thead) {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
}

.package-table :deep(.va-data-table__th) {
	padding: 1rem;
	color: white;
	font-weight: 600;
	text-align: left;
	border: 1px solid #dddddd;
	font-size: 0.875rem;
	text-transform: uppercase;
	letter-spacing: 0.025em;
}

/* Filas de la Tabla */
.package-table :deep(.va-data-table__tbody tr) {
	border-bottom: 1px solid #dddddd;
	transition: background-color 0.2s ease;
}

.package-table :deep(.va-data-table__tbody tr:hover) {
	background-color: #f0f8ff;
	transform: translateY(-1px);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.package-table :deep(.va-data-table__tbody tr:nth-of-type(even)) {
	background-color: #f3f3f3;
}

.package-table :deep(.va-data-table__tbody tr:nth-of-type(even):hover) {
	background-color: #e6f3ff;
}

.package-table :deep(.va-data-table__tbody tr:last-of-type) {
	border-bottom: 2px solid #1a4d42;
}

/* Celdas de la Tabla */
.package-table :deep(.va-data-table__td) {
	padding: 1rem;
	border: 1px solid #dddddd;
	vertical-align: middle;
}

/* Celdas Específicas */
.package-id-cell,
.package-label-cell,
.package-date-cell,
.package-status-cell,
.package-actions-cell {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.5rem;
	border-radius: 8px;
	transition: all 0.3s ease;
}

.package-id-cell:hover,
.package-label-cell:hover,
.package-date-cell:hover {
	background: rgba(0, 123, 255, 0.05);
}

/* Iconos de Celdas */
.package-icon,
.label-icon,
.date-icon {
	font-size: 1.125rem;
	flex-shrink: 0;
}

/* Textos de Celdas */
.package-id-text,
.package-label-text,
.package-date-text {
	font-weight: 500;
	color: #1a2a3a;
}

/* Celda de Estado */
.package-status-cell {
	justify-content: center;
}

/* Badges de Estado Mejorados */
.status-badge {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem 1rem;
	border-radius: 12px;
	font-size: 0.875rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	transition: all 0.3s ease;
}

.status-signed {
	background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
	color: #166534;
	border: 1px solid #86efac;
}

.status-pending {
	background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
	color: #92400e;
	border: 1px solid #fcd34d;
}

.status-icon {
	font-size: 1rem;
}

/* Celda de Acciones */
.package-actions-cell {
	justify-content: center;
}

.actions-container {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
	justify-content: center;
}

.action-popover {
	margin: 0 0.25rem;
}

/* Botones de Acción */
.action-button {
	transition: all 0.3s ease;
	border-radius: 8px;
	padding: 0.5rem;
	min-width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.action-button:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Botón Primario */
.btn-primary {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	color: white;
}

.btn-primary:hover {
	background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* Responsive Design */
@media (max-width: 768px) {
	.package-view-container {
		padding: 0.5rem;
	}

	.actions-container {
		flex-direction: column;
		gap: 0.25rem;
	}

	.action-button {
		min-width: 36px;
		height: 36px;
	}

	.package-table :deep(.va-data-table__th),
	.package-table :deep(.va-data-table__td) {
		padding: 0.75rem 0.5rem;
		overflow-x: auto;
		font-size: 0.875rem;
	}

	.package-id-cell,
	.package-label-cell,
	.package-date-cell,
	.package-status-cell,
	.package-actions-cell {
		padding: 0.25rem;
		gap: 0.5rem;
	}
}

/* Estados de Carga */
.package-table :deep(.va-data-table--loading) {
	opacity: 0.7;
	pointer-events: none;
}

/* Scrollbar Personalizado */
.package-table :deep(.va-data-table__table-wrapper)::-webkit-scrollbar {
	width: 8px;
}

.package-table :deep(.va-data-table__table-wrapper)::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

.package-table :deep(.va-data-table__table-wrapper)::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}

.package-table :deep(.va-data-table__table-wrapper)::-webkit-scrollbar-thumb:hover {
	background: #555;
}

/* Estilos para el Mensaje de Error */
.error-message {
	background: linear-gradient(135deg, #fffef5 0%, #fffef5 100%);
	border: 2px solid #f7c41d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f6e584;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #f5d165 0%, #f5d165 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #d8c426;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.error-content {
	color: #000000;
}

.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #d8c426;
}

.responsables-info {
	background: rgba(255, 255, 255, 0.8);
	border-radius: 12px;
	padding: 1.5rem;
	border: 1px solid #fef0ca;
}

.responsables-header {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-bottom: 1rem;
}

.responsables-header i {
	color: #d8c426;
	font-size: 1.25rem;
}

.responsables-header h4 {
	margin: 0;
	color: #000000;
	font-size: 1.1rem;
	font-weight: 600;
}

.responsables-list {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
}

.responsable-item {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.75rem 1rem;
	background: linear-gradient(135deg, #fff 0%, #f7fafc 100%);
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	transition: all 0.3s ease;
}

.responsable-item:hover {
	transform: translateX(4px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	border-color: #d8c426;
}

.responsable-item i {
	color: #d8c426;
	font-size: 1rem;
}

.responsable-item span {
	font-weight: 500;
	color: #2d3748;
}

/* Animaciones para el Error */
@keyframes slideInError {
	from {
		opacity: 0;
		transform: translateY(-20px) scale(0.95);
	}
	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}
</style>
